import type { ComponentProps, Dispatch, SetStateAction } from "react";
import MobileDrawerTabs from '../../components/navigation/MobileDrawerTabs';
import MobileDmPage from '../../components/navigation/MobileDmPage';
import MobileChannelsSheet from '../../components/navigation/MobileChannelsSheet';
import MobilePeopleSheet from '../../components/navigation/MobilePeopleSheet';

/** The mobile sheets (drawer pull-tabs, DM page, channels, people). Pure presentation: all state stays in ChatLayout. */
export type MobileSheetsProps = {
    headerSpaceLabel: ComponentProps<typeof MobileChannelsSheet>["headerSpaceLabel"];
    userQuickOpen: ComponentProps<typeof MobileDmPage>["userQuickOpen"];
    me: ComponentProps<typeof MobileDmPage>["me"];
    voids: ComponentProps<typeof MobileChannelsSheet>["voids"];
    hiddenSpaces: ComponentProps<typeof MobileDmPage>["hiddenSpaces"];
    currentVoidId: ComponentProps<typeof MobileChannelsSheet>["currentVoidId"];
    tabBgClass: ComponentProps<typeof MobileDrawerTabs>["tabBgClass"];
    showDrawerTabs: ComponentProps<typeof MobileDrawerTabs>["showDrawerTabs"];
    showPeople: ComponentProps<typeof MobileDrawerTabs>["showPeople"];
    unread: ComponentProps<typeof MobileDmPage>["unread"];
    dmSearch: ComponentProps<typeof MobileDmPage>["dmSearch"];
    dmSearchOpen: ComponentProps<typeof MobileDmPage>["dmSearchOpen"];
    friendsOnline: ComponentProps<typeof MobileDmPage>["friendsOnline"];
    friendsList: ComponentProps<typeof MobileDmPage>["friendsList"];
    dmStartBusyUserId: ComponentProps<typeof MobileDmPage>["dmStartBusyUserId"];
    activeDmVoids: ComponentProps<typeof MobileDmPage>["activeDmVoids"];
    msgsByKey: ComponentProps<typeof MobileDmPage>["msgsByKey"];
    notified: ComponentProps<typeof MobileDmPage>["notified"];
    getDmDisplayName: ComponentProps<typeof MobileDmPage>["getDmDisplayName"];
    channelPaneView: ComponentProps<typeof MobileChannelsSheet>["channelPaneView"];
    swipePanel: ComponentProps<typeof MobileChannelsSheet>["swipePanel"];
    chanDrawerProgress: ComponentProps<typeof MobileChannelsSheet>["chanDrawerProgress"];
    currentSpace: ComponentProps<typeof MobileChannelsSheet>["currentSpace"];
    currentSpaceOnlineCount: ComponentProps<typeof MobileChannelsSheet>["currentSpaceOnlineCount"];
    dmFriendSearch: ComponentProps<typeof MobileChannelsSheet>["dmFriendSearch"];
    filteredFriendsForDmSearch: ComponentProps<typeof MobileChannelsSheet>["filteredFriendsForDmSearch"];
    channels: ComponentProps<typeof MobileChannelsSheet>["channels"];
    currentChannelId: ComponentProps<typeof MobileChannelsSheet>["currentChannelId"];
    sidebarChannels: ComponentProps<typeof MobileChannelsSheet>["sidebarChannels"];
    channelsLoading: boolean;
    peopleLoading: boolean;
    channelFolders: ComponentProps<typeof MobileChannelsSheet>["folders"];
    getVoiceNamesForChannel: ComponentProps<typeof MobileChannelsSheet>["getVoiceNamesForChannel"];
    usersDrawerProgress: ComponentProps<typeof MobilePeopleSheet>["usersDrawerProgress"];
    channelVisibleMembersWithPresence: ComponentProps<typeof MobilePeopleSheet>["channelVisibleMembersWithPresence"];
    onlineChannelMembers: ComponentProps<typeof MobilePeopleSheet>["onlineChannelMembers"];
    offlineChannelMembers: ComponentProps<typeof MobilePeopleSheet>["offlineChannelMembers"];
    dmPageOpen: boolean;
    chanSheetOpen: boolean;
    usersSheetOpen: boolean;
    isDmSpace: boolean;
    isSpaceOwner: boolean;
    setChanSheetOpen: (open: boolean) => void;
    setUsersSheetOpen: (open: boolean) => void;
    setDmPageOpen: (open: boolean) => void;
    setUserQuickOpen: Dispatch<SetStateAction<boolean>>;
    setDmSearch: ComponentProps<typeof MobileDmPage>["setDmSearch"];
    setDmSearchOpen: ComponentProps<typeof MobileDmPage>["setDmSearchOpen"];
    setDmFriendSearch: ComponentProps<typeof MobileChannelsSheet>["setDmFriendSearch"];
    setCurrentVoidId: (id: string) => void;
    setCurrentChannelId: (id: string) => void;
    setChannelPaneView: (view: "spaces" | "dms") => void;
    switchVoid: (id: string) => void;
    switchChannel: (id: string) => void;
    handleChannelReorder: ComponentProps<typeof MobileChannelsSheet>["onChannelReorder"];
    handleFolderReorder: ComponentProps<typeof MobileChannelsSheet>["onFolderReorder"];
    onOpenSettings: (section?: string) => void;
    startDmWithUser: (userId: string) => unknown;
    setFriendsOpen: (open: boolean) => void;
    onOpenProfile: (targetId: string) => void;
};

export function MobileSheets({
  headerSpaceLabel, userQuickOpen, me, voids, hiddenSpaces, currentVoidId, tabBgClass, showDrawerTabs, showPeople, unread, dmSearch, dmSearchOpen, friendsOnline, friendsList, dmStartBusyUserId, activeDmVoids, msgsByKey, notified, getDmDisplayName, channelPaneView, swipePanel, chanDrawerProgress, currentSpace, currentSpaceOnlineCount, dmFriendSearch, filteredFriendsForDmSearch, channels, currentChannelId, sidebarChannels, channelsLoading, peopleLoading, channelFolders, getVoiceNamesForChannel, usersDrawerProgress, channelVisibleMembersWithPresence, onlineChannelMembers, offlineChannelMembers, dmPageOpen, chanSheetOpen, usersSheetOpen, isDmSpace, isSpaceOwner, setChanSheetOpen, setUsersSheetOpen, setDmPageOpen, setUserQuickOpen, setDmSearch, setDmSearchOpen, setDmFriendSearch, setCurrentVoidId, setCurrentChannelId, setChannelPaneView, switchVoid, switchChannel, handleChannelReorder, handleFolderReorder, onOpenSettings, startDmWithUser, setFriendsOpen, onOpenProfile
}: MobileSheetsProps) {
  return (
    <>
      {/* Mobile drawer pull-tabs */}
      {!isDmSpace && (
        <MobileDrawerTabs
          tabBgClass={tabBgClass}
          showDrawerTabs={showDrawerTabs}
          showPeople={showPeople}
          onOpenChannels={() => { setChanSheetOpen(true); setUsersSheetOpen(false); }}
          onOpenPeople={() => { setUsersSheetOpen(true); setChanSheetOpen(false); }}
        />
      )}

      {/* Mobile: DM page overlay */}
      {dmPageOpen && (
        <MobileDmPage
          spaces={voids}
          hiddenSpaces={hiddenSpaces}
          currentVoidId={currentVoidId}
          unread={unread}
          me={me}
          userQuickOpen={userQuickOpen}
          onToggleQuickSettings={() => setUserQuickOpen(p => !p)}
          onClose={() => setDmPageOpen(false)}
          onGoLanding={() => { setDmPageOpen(false); setCurrentVoidId(""); setCurrentChannelId("general"); setChannelPaneView('spaces'); }}
          onSwitchSpace={(id) => { switchVoid(id); setDmPageOpen(false); }}
          dmSearch={dmSearch}
          dmSearchOpen={dmSearchOpen}
          setDmSearch={setDmSearch}
          setDmSearchOpen={setDmSearchOpen}
          friendsOnline={friendsOnline}
          friendsList={friendsList}
          dmStartBusyUserId={dmStartBusyUserId}
          activeDmVoids={activeDmVoids}
          msgsByKey={msgsByKey}
          notified={notified}
          tabBgClass={tabBgClass}
          getDmDisplayName={getDmDisplayName}
          onStartDm={(id) => { void startDmWithUser(id); setDmPageOpen(false); }}
          onOpenFriends={() => setFriendsOpen(true)}
          onOpenDm={(id) => { switchVoid(id); switchChannel('chat'); setDmPageOpen(false); }}
        />
      )}

      {/* Mobile: Channels sheet (gesture-aware) */}
      {(channelPaneView === 'dms' || !isDmSpace) && (chanSheetOpen || swipePanel === 'chan') && (
        <MobileChannelsSheet
          channelPaneView={channelPaneView}
          isDmSpace={isDmSpace}
          swipePanel={swipePanel}
          chanDrawerProgress={chanDrawerProgress}
          currentSpace={currentSpace}
          voids={voids}
          currentVoidId={currentVoidId}
          headerSpaceLabel={headerSpaceLabel}
          currentSpaceOnlineCount={currentSpaceOnlineCount}
          activeDmVoids={activeDmVoids}
          dmFriendSearch={dmFriendSearch}
          setDmFriendSearch={setDmFriendSearch}
          filteredFriendsForDmSearch={filteredFriendsForDmSearch}
          dmStartBusyUserId={dmStartBusyUserId}
          unread={unread}
          getDmDisplayName={getDmDisplayName}
          channels={channels}
          currentChannelId={currentChannelId}
          sidebarChannels={sidebarChannels}
          loading={channelsLoading}
          folders={channelFolders}
          canManageChannels={isSpaceOwner && !isDmSpace}
          getVoiceNamesForChannel={getVoiceNamesForChannel}
          onClose={() => setChanSheetOpen(false)}
          onOpenDm={(id) => { switchVoid(id); switchChannel('chat'); setChanSheetOpen(false); }}
          onStartDm={(id) => { void startDmWithUser(id); }}
          onSwitchChannel={(id) => { switchChannel(id); setChanSheetOpen(false); }}
          onChannelReorder={handleChannelReorder}
          onFolderReorder={handleFolderReorder}
          onOpenChannelSettings={() => { onOpenSettings('spaces'); setChanSheetOpen(false); }}
        />
      )}

      {/* Mobile: People sheet (gesture-aware) */}
      {(showPeople || usersSheetOpen || (!currentVoidId && friendsOnline.length > 0)) && (usersSheetOpen || swipePanel === 'users') && (
        <MobilePeopleSheet
          usersDrawerProgress={usersDrawerProgress}
          swipePanel={swipePanel}
          voids={voids}
          currentVoidId={currentVoidId}
          headerSpaceLabel={headerSpaceLabel}
          isDmSpace={isDmSpace}
          currentSpaceOnlineCount={currentSpaceOnlineCount}
          channelVisibleMembersWithPresence={channelVisibleMembersWithPresence}
          loading={peopleLoading}
          onlineChannelMembers={onlineChannelMembers}
          offlineChannelMembers={offlineChannelMembers}
          onClose={() => setUsersSheetOpen(false)}
          onOpenProfile={(id) => { setUsersSheetOpen(false); onOpenProfile(id); }}
        />
      )}
    </>
  );
}
